import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Chip,
  Tooltip,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  Flight,
  GroupWork,
  AddCircleOutline,
  CenterFocusStrong,
  ViewTimeline,
  TableView,
  Download,
  Publish as PublishIcon,
  Delete,
  FilterList,
  WarningAmber,
} from '@mui/icons-material';
import { Group, Panel } from 'react-resizable-panels';
import ResizeSeparator from 'components/ResizeSeparator';
import {
  useGetSeasonsQuery,
  useGetScheduledFlightsQuery,
  useUpdateScheduledFlightMutation,
  useCreateScheduledFlightMutation,
  useDeleteScheduledFlightsMutation,
  usePublishScheduleMutation,
} from 'features/apiSlice';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import api from 'state/axios';
import PublishDialog from './timetable/PublishDialog';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import {
  DAY_LABELS,
  TOTAL_MIN,
  ROW_H,
  RULER_H,
  RULER_TOP,
  AC_PALETTE, 
  MIN_ZOOM, 
  MAX_ZOOM, 
  toHHMM,
} from './constants';
import Ruler from './Ruler';
import GanttBar from './GanttBar';
import RouteRowLabel from './RouteRowLabel';
import FlightDetailStrip from './FlightDetailStrip';
import FlightFormDialog from './FlightFormDialog';
import TimetableView from './timetable';


const SchedulePlanner = () => {
  useAuthCheck();
  const user  = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canCreate = hasRight(permissionMap, 'SchedulePlanner', 'create', false);
  const canUpdate = hasRight(permissionMap, 'SchedulePlanner', 'update', false);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedSeasonId, setSelectedSeasonId]  = useState('');
  const [selectedDay,      setSelectedDay]       = useState('1');
  const [groupBy,          setGroupBy]           = useState('route');
  const [zoom,             setZoom]              = useState(1);
  const [filterAcType,     setFilterAcType]      = useState('');
  const [selectedFlight,   setSelectedFlight]    = useState(null);
  const [formDialog,       setFormDialog]        = useState({ open: false, data: null, prefill: null });
  const [publishDialog,    setPublishDialog]     = useState(false);
  const [timetableSel,     setTimetableSel]      = useState({ has: false, canDelete: false });
  const [timetableFilter,  setTimetableFilter]   = useState({ count: 0, conflicts: 0 });

  const scrollRef      = useRef(null);
  const panelWrapRef   = useRef(null);
  const timetableRef   = useRef(null);
  const [containerW, setContainerW] = useState(0);

  // Measure from the wrapper OUTSIDE overflow:auto so scrollbar never affects width
  useEffect(() => {
    const el = panelWrapRef.current;
    if (!el) return;
    let rafId;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setContainerW((prev) => (prev === w ? prev : w)));
    });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(rafId); };
  }, [activeTab]);

  useLayoutEffect(() => {
    if (activeTab !== 0) return;
    const el = panelWrapRef.current;
    if (el) setContainerW(Math.round(el.getBoundingClientRect().width));
  }, [activeTab]);

  /* ── Data ──────────────────────────────────────────────── */
  const { data: seasonsData } = useGetSeasonsQuery({ limit: 200 });
  const seasons = useMemo(() => seasonsData?.data || [], [seasonsData]);

  useEffect(() => {
    if (!selectedSeasonId && seasons.length > 0) {
      const active = seasons.find((s) => s.status === 'active') || seasons[0];
      if (active) setSelectedSeasonId(active._id);
    }
  }, [seasons, selectedSeasonId]);

  const { data: flightData, refetch } = useGetScheduledFlightsQuery(
    { seasonId: selectedSeasonId, limit: 500 },
    { skip: !selectedSeasonId, refetchOnMountOrArgChange: true }
  );
  const allFlights = useMemo(() => flightData?.data || [], [flightData]);

  const [updateFlight]    = useUpdateScheduledFlightMutation();
  const [createFlight]    = useCreateScheduledFlightMutation();
  const [deleteFlights]   = useDeleteScheduledFlightsMutation();
  const [publishSchedule] = usePublishScheduleMutation();

  const selectedSeason = useMemo(
    () => seasons.find((s) => s._id === selectedSeasonId),
    [seasons, selectedSeasonId],
  );

  const handleExportSSIM = useCallback(async () => {
    if (!selectedSeasonId) return;
    try {
      const res = await api.get('/scheduled-flights/export-ssim', {
        params: { seasonId: selectedSeasonId },
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `SSIM_${selectedSeason?.seasonCode || 'export'}_${format(new Date(), 'yyyyMMdd')}.ssim`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('SSIM exported.');
    } catch {
      toast.error('Export failed.');
    }
  }, [selectedSeasonId, selectedSeason]);

  const handlePublish = useCallback(async (values, { setSubmitting, resetForm }) => {
    try {
      const result = await publishSchedule({ seasonId: selectedSeasonId, ...values }).unwrap();
      toast.success(`Published: ${result.created} created, ${result.matched} already existed.`);
      resetForm();
      setPublishDialog(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Publish failed.');
    } finally {
      setSubmitting(false);
    }
  }, [publishSchedule, selectedSeasonId]);

  /* ── Derived data ──────────────────────────────────────── */
  const dayFlights = useMemo(
    () => allFlights.filter((f) =>
      f.daysOfOperation?.includes(selectedDay) &&
      f.status !== 'cancelled' &&
      (!filterAcType || f.acType === filterAcType)
    ),
    [allFlights, selectedDay, filterAcType]
  );

  const rows = useMemo(() => {
    const map = new Map();
    for (const f of dayFlights) {
      const key = groupBy === 'route' ? `${f.dep}-${f.arr}` : (f.acType || 'N/A');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [dayFlights, groupBy]);

  const acTypeColorMap = useMemo(() => {
    const types = [...new Set(allFlights.map((f) => f.acType).filter(Boolean))];
    return Object.fromEntries(types.map((t, i) => [t, AC_PALETTE[i % AC_PALETTE.length]]));
  }, [allFlights]);

  const totalW = useMemo(() => Math.max(containerW, 1) * zoom, [containerW, zoom]);

  const formInitial = useMemo(() => {
    if (formDialog.data) {
      const d = formDialog.data;
      return {
        seasonId: d.seasonId || selectedSeasonId,
        carrier: d.carrier || '', flightNo: d.flightNo || '',
        dep: d.dep || '', arr: d.arr || '',
        std: d.std || '', sta: d.sta || '',
        daysOfOperation: d.daysOfOperation || '1234567',
        acType: d.acType || '', serviceType: d.serviceType || 'J',
        status: d.status || 'planned',
        effectiveFrom: d.effectiveFrom ? d.effectiveFrom.slice(0, 10) : '',
        effectiveTo:   d.effectiveTo   ? d.effectiveTo.slice(0, 10)   : '',
        codeshare: !!d.codeshare, slotCoordinated: !!d.slotCoordinated,
        notes: d.notes || '',
      };
    }
    const p = formDialog.prefill || {};
    return {
      seasonId: selectedSeasonId,
      carrier: p.carrier || '', flightNo: p.flightNo || '',
      dep: p.dep || '', arr: p.arr || '',
      std: p.std || '', sta: p.sta || '',
      daysOfOperation: p.daysOfOperation || selectedDay,
      acType: p.acType || '', serviceType: 'J',
      status: 'planned',
      effectiveFrom: '', effectiveTo: '',
      codeshare: false, slotCoordinated: false,
      notes: '',
    };
  }, [formDialog, selectedSeasonId, selectedDay]);

  /* ── Handlers ──────────────────────────────────────────── */
  const handleDragEnd = useCallback(async (id, newStd, newSta) => {
    try {
      await updateFlight({ id, std: newStd, sta: newSta }).unwrap();
      refetch();
    } catch {
      toast.error('Failed to update time!');
    }
  }, [updateFlight, refetch]);

  const handleDeleteFlight = useCallback(async (flight) => {
    try {
      await deleteFlights([flight._id]).unwrap();
      toast.success('Flight deleted.');
      setSelectedFlight(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed.');
    }
  }, [deleteFlights, refetch]);

  const handleDuplicateFlight = useCallback((flight) => {
    const { _id, __v, createdAt, updatedAt, frequency, sectorType, blockTime, nextDay, ...rest } = flight;
    setFormDialog({ open: true, data: null, prefill: rest });
  }, []);

  const handleSelect       = useCallback((flight) => setSelectedFlight((prev) => prev?._id === flight._id ? null : flight), []);
  const handleEditRequest  = useCallback((flight) => setFormDialog({ open: true, data: flight, prefill: null }), []);

  const handleRowClick = useCallback((e, dep, arr, rowEl) => {
    if (!canCreate) return;
    const rect   = rowEl.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const mins   = Math.round((clickX / totalW) * TOTAL_MIN / 15) * 15;
    setFormDialog({ 
      open: true, 
      data: null, 
      prefill: { dep, arr, std: toHHMM(mins), sta: toHHMM(mins + 90), daysOfOperation: selectedDay } 
    });
  }, [canCreate, totalW, selectedDay]);

  const handleSave = useCallback(async (values, { setSubmitting, resetForm }) => {
    try {
      if (formDialog.data) {
        await updateFlight({ id: formDialog.data._id, ...values }).unwrap();
        toast.success('Updated.');
      } else {
        await createFlight(values).unwrap();
        toast.success('Created.');
      }
      resetForm();
      setFormDialog({ open: false, data: null, prefill: null });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Save failed!');
    } finally {
      setSubmitting(false);
    }
  }, [formDialog.data, updateFlight, createFlight, refetch]);

  const closeForm   = useCallback(() => setFormDialog({ open: false, data: null, prefill: null }), []);
  const scrollTo6am = () => { if (scrollRef.current) scrollRef.current.scrollLeft = 6 * 60 * zoom; };
  const handleTimetableSelection = useCallback((has, canDel) => setTimetableSel({ has, canDelete: canDel }), []);
  const handleTimetableFilter    = useCallback((count, conflicts) => setTimetableFilter({ count, conflicts }), []);

  /* ── Keyboard shortcuts ────────────────────────────────── */
  useEffect(() => {
    const handleKey = (e) => {
      if (formDialog.open) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        setSelectedFlight(null);
      } else if (e.key === 'n' || e.key === 'N') {
        if (!selectedSeasonId || !canCreate) return;
        e.preventDefault();
        setFormDialog({ open: true, data: null, prefill: null });
      } else if (e.key === 'e' || e.key === 'E') {
        if (selectedFlight && canUpdate) setFormDialog({ open: true, data: selectedFlight, prefill: null });
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        if (selectedFlight && canCreate) handleDuplicateFlight(selectedFlight);
      } else if (e.key === 'Delete') {
        if (selectedFlight && canUpdate) handleDeleteFlight(selectedFlight);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    formDialog.open, 
    selectedSeasonId, 
    canCreate, 
    canUpdate, 
    selectedFlight, 
    handleDeleteFlight, 
    handleDuplicateFlight
  ]);
  

  /* ── Render ────────────────────────────────────────────── */
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Schedule Planner"
          subtitle={activeTab === 0 ? 'Gantt View' : 'Timetable'}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            select 
            size="small" 
            label="Season" 
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  seasons?.find((s) => s._id === selected)?.seasonCode || '',
              },
            }}
          >
            {seasons?.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.seasonCode} - {s.seasonName}
              </MenuItem>
            ))}
          </TextField>

          {/* Tab toggle */}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={activeTab}
            onChange={(_, v) => v !== null && setActiveTab(v)}
          >
            <Tooltip title="Gantt View" arrow>
              <ToggleButton value={0} sx={{ px: 1 }}>
                <ViewTimeline fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Timetable" arrow>
              <ToggleButton value={1} sx={{ px: 1 }}>
                <TableView fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          
          {!isMobile && (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Export SSIM" arrow>
                <span>
                  <StyledButton 
                    disabled={!selectedSeasonId} 
                    onClick={handleExportSSIM}
                  >
                    <Download />
                  </StyledButton>
                </span>
              </Tooltip>
              <Tooltip title="Publish" arrow>
                <span>
                  <StyledButton 
                    disabled={!selectedSeasonId} 
                    onClick={() => setPublishDialog(true)}
                  >
                    <PublishIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* Filter — timetable tab only */}
          {activeTab === 1 && (
            <>
              {timetableFilter.conflicts > 0 && (
                <Tooltip title={`${timetableFilter.conflicts} conflict(s)`} arrow>
                  <span>
                    <StyledButton 
                      onClick={() => timetableRef.current?.openConflicts()} 
                      sx={{ color: 'warning.main' }}
                    >
                      <WarningAmber />
                    </StyledButton>
                  </span>
                </Tooltip>
              )}
              
              <Tooltip title="Filter" arrow>
                <span>
                  <StyledButton
                    badge={timetableFilter.count || undefined}
                    onClick={() => timetableRef.current?.openFilter()}
                    sx={timetableFilter.count > 0 ? { color: 'primary.main', borderColor: 'primary.main' } : {}}
                  >
                    <FilterList />
                  </StyledButton>
                </span>
              </Tooltip>
            </>
          )}

          {/* Delete selected — timetable tab only */}
          {activeTab === 1 && timetableSel.has && (
            <Tooltip title="Delete selected" arrow>
              <span>
                <StyledButton
                  disabled={!timetableSel.canDelete}
                  onClick={() => timetableRef.current?.deleteSelected()}
                  sx={{ color: 'error.main' }}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>
          )}

          {/* Add flight — shared */}
          <Tooltip title="Add flight (N)" arrow>
            <span>
              <StyledButton
                disabled={!selectedSeasonId}
                onClick={() => {
                  if (activeTab === 0) setFormDialog({ open: true, data: null, prefill: null });
                  else timetableRef.current?.openAddForm();
                }}
              >
                <AddCircleOutline />
              </StyledButton>
            </span>
          </Tooltip>
        </Box>
      </FlexBetween>

      {activeTab === 1 && (
        <TimetableView
          ref={timetableRef}
          selectedSeasonId={selectedSeasonId}
          seasons={seasons}
          onSelectionChange={handleTimetableSelection}
          onFilterChange={handleTimetableFilter}
        />
      )}

      {activeTab === 0 && (
        <>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            sx={{
              gridColumn: 'span 12',
              height: LAYOUT_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {/* Controls */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <ToggleButtonGroup exclusive size="small" value={selectedDay} onChange={(_, v) => v && setSelectedDay(v)}>
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <ToggleButton key={d} value={String(d)} sx={{ px: 1, fontSize: '0.68rem' }}>
                    {DAY_LABELS[d].slice(0, 2)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <ToggleButtonGroup exclusive size="small" value={groupBy} onChange={(_, v) => v && setGroupBy(v)}>
                <Tooltip title="Group by route"><ToggleButton value="route" sx={{ px: 1 }}><Flight fontSize="small" /></ToggleButton></Tooltip>
                <Tooltip title="Group by AC type"><ToggleButton value="actype" sx={{ px: 1 }}><GroupWork fontSize="small" /></ToggleButton></Tooltip>
              </ToggleButtonGroup>

              {/* Zoom */}
              <Box display="flex" alignItems="center" gap={0.3}>
                <Tooltip title="Zoom out"><StyledButton size="small" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.25).toFixed(2)))}><ZoomOut fontSize="small" /></StyledButton></Tooltip>
                <Typography sx={{ minWidth: 34, textAlign: 'center', fontSize: '0.72rem', fontFamily: 'monospace' }}>{Math.round(zoom * 100)}%</Typography>
                <Tooltip title="Zoom in"><StyledButton size="small" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.25).toFixed(2)))}><ZoomIn fontSize="small" /></StyledButton></Tooltip>
                <Tooltip title="Scroll to 06:00"><StyledButton size="small" onClick={scrollTo6am}><CenterFocusStrong fontSize="small" /></StyledButton></Tooltip>
              </Box>

              {/* AC Type — clickable filter */}
              <Box display="flex" gap={0.4} flexWrap="wrap">
                {Object.entries(acTypeColorMap).map(([type, color]) => {
                  const active = filterAcType === type;
                  return (
                    <Chip 
                      key={type} 
                      label={type} 
                      size="small"
                      onClick={() => setFilterAcType(active ? '' : type)}
                      sx={{
                        bgcolor: active ? color : `${color}66`, 
                        '&:hover': { bgcolor: color },
                        color: 'text.primary',
                        border: active ? `2px solid ${color}` : '1px solid transparent',
                        fontSize: '0.62rem', 
                        cursor: 'pointer', 
                        transition: 'all 0.15s',
                      }} />
                  );
                })}
                {filterAcType && (
                  <Chip 
                    variant="outlined"
                    size="small" 
                    label="× Clear" 
                    onClick={() => setFilterAcType('')}
                    sx={{ fontSize: '0.6rem', cursor: 'pointer' }} />
                )}
              </Box>

            </Box>

            {/* Main board */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, gap: selectedFlight ? 0.5 : 0 }}>

              {/* ── Gantt ── */}
              <Box sx={{ flex: 1, minHeight: 0, borderRadius: 1, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                    <Group direction="horizontal" style={{ height: '100%' }}>

                      {/* Left: label column */}
                      <Panel defaultSize={10} minSize={0}>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          <Box sx={{ height: RULER_H, flexShrink: 0, bgcolor: 'background.alt', borderBottom: (t) => `2px solid ${t.palette.divider}`, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ height: RULER_TOP, display: 'flex', alignItems: 'center', px: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                              <Typography sx={{ fontSize: '0.65rem', color: 'warning.main', fontFamily: 'monospace', fontWeight: 600, letterSpacing: 0.5, userSelect: 'none' }}>
                                {DAY_LABELS[Number(selectedDay)] || ''}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 1 }}>
                              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace', letterSpacing: 1.2, textTransform: 'uppercase', userSelect: 'none' }}>
                                {groupBy === 'route' ? 'Route' : 'AC Type'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ overflowY: 'hidden', flex: 1 }}>
                            {rows.map(([key, rowFlights]) => (
                              <RouteRowLabel 
                                key={key} 
                                rowKey={key} 
                                flights={rowFlights} 
                                groupBy={groupBy} 
                                theme={theme} 
                              />
                            ))}
                          </Box>
                        </Box>
                      </Panel>

                      <ResizeSeparator gripVisible={false} />

                      {/* Right: scrollable timeline */}
                      <Panel defaultSize={90} minSize={0}>
                        <Box ref={panelWrapRef} sx={{ height: '100%' }}>
                        <Box ref={scrollRef} sx={{ height: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                          <Box sx={{ width: totalW, minWidth: totalW, position: 'relative' }}>
                            <Ruler totalW={totalW} />

                            {rows.length === 0 ? (
                              <FlexCenter height={120}>
                                <Typography variant="body2" color="text.secondary">
                                  {selectedSeasonId ? `No flights on ${DAY_LABELS[Number(selectedDay)]}` : 'Select a season'}
                                </Typography>
                              </FlexCenter>
                            ) : (
                              rows.map(([key, rowFlights], ri) => {
                                const [dep, arr] = key.includes('-') ? key.split('-') : [null, null];
                                return (
                                  <Box
                                    key={key}
                                    sx={{
                                      position: 'relative', 
                                      height: ROW_H, 
                                      width: totalW,
                                      borderBottom: `1px solid ${theme.palette.divider}`,
                                      bgcolor: ri % 2 === 0 ? 'transparent' : theme.palette.action.hover,
                                      cursor: canCreate ? 'crosshair' : 'default',
                                    }}
                                    onClick={(e) => {
                                      if (e.target === e.currentTarget)
                                        handleRowClick(e, dep || key, arr || '', e.currentTarget);
                                    }}
                                  >
                                    {/* Night shading 00-06 */}
                                    <Box sx={{ position: 'absolute', left: 0, width: (6 * 60 / TOTAL_MIN) * totalW, top: 0, bottom: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,60,0.07)', pointerEvents: 'none' }} />
                                    {/* Night shading 22-24 */}
                                    <Box sx={{ position: 'absolute', left: (22 * 60 / TOTAL_MIN) * totalW, right: 0, top: 0, bottom: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,60,0.07)', pointerEvents: 'none' }} />
                                    {/* Hour grid */}
                                    {Array.from({ length: 48 }, (_, i) => (
                                      <Box key={i} sx={{ position: 'absolute', left: (i * 30 / TOTAL_MIN) * totalW, top: 0, bottom: 0, width: 1, bgcolor: theme.palette.divider, opacity: i % 2 === 0 ? 0.45 : 0.15, pointerEvents: 'none' }} />
                                    ))}
                                    {/* Flight bars */}
                                    {rowFlights.map((f) => (
                                      <GanttBar
                                        key={f._id}
                                        flight={f}
                                        totalW={totalW}
                                        color={acTypeColorMap[f.acType] || 'blue.main'}
                                        isSelected={selectedFlight?._id === f._id}
                                        onSelect={handleSelect}
                                        onEditRequest={handleEditRequest}
                                        onDragEnd={handleDragEnd}
                                      />
                                    ))}
                                  </Box>
                                );
                              })
                            )}
                          </Box>
                        </Box>
                        </Box>
                      </Panel>
                    </Group>
              </Box>

              {/* ── Detail strip — conditional, no splitter ── */}
              {selectedFlight && (
                <Box sx={{ height: 125, flexShrink: 0 }}>
                  <FlightDetailStrip
                    flight={selectedFlight}
                    onEdit={handleEditRequest}
                    onDelete={handleDeleteFlight}
                    onDuplicate={handleDuplicateFlight}
                    onClose={() => setSelectedFlight(null)}
                    canUpdate={canUpdate}
                    canCreate={canCreate}
                  />
                </Box>
              )}

            </Box>
          </Box>
        </Box>

        <FlightFormDialog
          open={formDialog.open}
          onClose={closeForm}
          data={formDialog.data}
          initialValues={formInitial}
          seasons={seasons}
          onSave={handleSave}
        />
        </>
      )}

      <PublishDialog
        open={publishDialog}
        onClose={() => setPublishDialog(false)}
        onPublish={handlePublish}
      />
    </Box>
  );
};

export default SchedulePlanner;
